import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import DashNav from "@/components/chalann/DashNav";
import { listMasterProducts, getMasterFacets, adoptMasterProducts, adoptByBrand, adoptByCategory, getStore } from "@/lib/api";
import { Search, Check, Plus, Library, Tag, LayoutGrid } from "lucide-react";

/**
 * Master catalog browser: search + subcategory/brand filters + multi-select adopt.
 * Products the store already has appear disabled with a "Ya en tu tienda" chip.
 */
export default function Catalogo() {
  const nav = useNavigate();
  const store = getStore();
  const [facets, setFacets] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [subcat, setSubcat] = useState("");
  const [brand, setBrand] = useState("");
  const [sel, setSel] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [adopting, setAdopting] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 60;

  useEffect(() => {
    if (!store?.id) { nav("/setup"); return; }
    getMasterFacets().then(setFacets).catch(() => {});
  }, [nav, store?.id]);

  useEffect(() => {
    setLoading(true);
    listMasterProducts({ q, category, subcategory: subcat, brand, page, limit })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch(() => toast.error("No se pudo cargar el catálogo"))
      .finally(() => setLoading(false));
  }, [q, category, subcat, brand, page]);

  useEffect(() => { setPage(1); }, [q, category, subcat, brand]);

  const toggle = (id) => {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAllVisible = () => {
    setSel((s) => {
      const allIds = items.map((i) => i.id);
      const allSelected = allIds.every((id) => s.has(id));
      const n = new Set(s);
      if (allSelected) allIds.forEach((id) => n.delete(id));
      else allIds.forEach((id) => n.add(id));
      return n;
    });
  };

  const doAdopt = async (kind, arg) => {
    setAdopting(true);
    try {
      let r;
      if (kind === "ids") r = await adoptMasterProducts([...sel]);
      else if (kind === "brand") r = await adoptByBrand(arg);
      else if (kind === "cat") r = await adoptByCategory(arg.category, arg.subcategory);
      const created = r.created || 0;
      if (created > 0) {
        toast.success(
          `${created} ${created === 1 ? "producto agregado" : "productos agregados"} · Toca "Ponerles precio" para configurarlos`,
          {
            duration: 6000,
            action: {
              label: "Ponerles precio",
              onClick: () => nav("/precios-iniciales"),
            },
          }
        );
      } else {
        toast.info(r.message || "Ya tenías esos productos en tu tienda");
      }
      setSel(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo agregar"); }
    finally { setAdopting(false); }
  };

  const subcats = useMemo(() => {
    if (!facets) return [];
    return facets.categories.flatMap((c) => c.subcategories.map((s) => ({ ...s, category: c.category })));
  }, [facets]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="catalogo">
      <DashNav />
      <div className="grain-overlay" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 py-8 md:py-12">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-2">/ Catálogo maestro</p>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-none mb-3">
            No captures desde <span className="text-[#ccff00]">cero</span>.
          </h1>
          <p className="text-white/60 max-w-2xl">
            Ya tenemos <span className="text-white">{total}</span> productos listos. Sólo dinos cuáles vendes; tú pones precio y existencias después.
          </p>
        </motion.div>

        {/* Search + filters */}
        <div className="mt-8 space-y-3">
          <div className="flex items-center gap-2 bg-black/60 border border-white/10 rounded-2xl px-3 py-3">
            <Search size={16} className="text-white/40" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Busca por marca, sabor, tamaño o código de barras — ej. "coca 600", "electrolit fresa"'
              className="flex-1 bg-transparent outline-none text-sm placeholder-white/30"
              data-testid="catalogo-search"
            />
          </div>

          {facets && (
            <div className="flex items-start gap-4 flex-wrap">
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase mr-1">Categoría:</span>
                <FilterChip label="Todas" active={!category} onClick={() => { setCategory(""); setSubcat(""); }} tid="filter-cat-all" />
                {facets.categories.map((c) => (
                  <FilterChip key={c.category} label={`${c.category} · ${c.count}`} active={category === c.category} onClick={() => { setCategory(c.category); setSubcat(""); }} tid={`filter-cat-${c.category}`} />
                ))}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase mr-1">Subcategoría:</span>
                <FilterChip label="Todas" active={!subcat} onClick={() => setSubcat("")} tid="filter-subcat-all" />
                {subcats
                  .filter((s) => !category || s.category === category)
                  .slice(0, 15).map((s) => (
                    <FilterChip key={`${s.category}-${s.subcategory}`} label={`${s.subcategory} · ${s.count}`} active={subcat === s.subcategory} onClick={() => setSubcat(s.subcategory)} tid={`filter-subcat-${s.subcategory}`} />
                  ))}
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <span className="font-mono text-[10px] tracking-widest text-white/40 uppercase mr-1">Marca:</span>
                <FilterChip label="Todas" active={!brand} onClick={() => setBrand("")} tid="filter-brand-all" />
                {facets.brands.slice(0, 15).map((b) => (
                  <FilterChip key={b.brand} label={b.brand} active={brand === b.brand} onClick={() => setBrand(b.brand)} tid={`filter-brand-${b.brand}`} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bulk action bar */}
        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <button onClick={toggleAllVisible} className="text-[11px] font-mono uppercase tracking-widest px-3 py-2 rounded-full border border-white/10 text-white/70 hover:text-white" data-testid="catalogo-select-visible">
            Marcar/desmarcar visibles ({items.length})
          </button>
          <button
            disabled={!sel.size || adopting}
            onClick={() => doAdopt("ids")}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ccff00] hover:bg-[#dfff40] text-black font-bold text-sm disabled:opacity-40"
            data-testid="catalogo-adopt-selected"
          >
            <Plus size={14} /> Agregar seleccionados ({sel.size})
          </button>
          {brand && (
            <button onClick={() => doAdopt("brand", brand)} disabled={adopting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#ccff00]/40 text-[#ccff00] text-[11px] font-mono uppercase tracking-widest hover:bg-[#ccff00]/10 disabled:opacity-40" data-testid="catalogo-adopt-brand">
              <Tag size={12} /> Toda la marca {brand}
            </button>
          )}
          {subcat && (
            <button onClick={() => doAdopt("cat", { category: category || "Bebidas", subcategory: subcat })} disabled={adopting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white text-[11px] font-mono uppercase tracking-widest disabled:opacity-40" data-testid="catalogo-adopt-subcat">
              <LayoutGrid size={12} /> Toda la subcategoría {subcat}
            </button>
          )}
          {!subcat && category && (
            <button onClick={() => doAdopt("cat", { category })} disabled={adopting} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/70 hover:text-white text-[11px] font-mono uppercase tracking-widest disabled:opacity-40" data-testid="catalogo-adopt-cat">
              <LayoutGrid size={12} /> Toda la categoría {category}
            </button>
          )}
          <span className="ml-auto text-white/40 text-xs">Página {page} de {totalPages}</span>
        </div>

        {/* Grid */}
        {loading ? (
          <p className="text-center py-16 text-white/40 text-sm">Cargando…</p>
        ) : items.length === 0 ? (
          <p className="text-center py-16 text-white/40 text-sm">Sin resultados con estos filtros.</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="catalogo-grid">
            {items.map((mp) => (
              <label key={mp.id} className={`relative border rounded-2xl p-4 cursor-pointer transition-colors ${sel.has(mp.id) ? "border-[#ccff00] bg-[#ccff00]/10" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`} data-testid={`master-${mp.id}`}>
                <input type="checkbox" className="absolute top-3 right-3 accent-[#ccff00] w-4 h-4" checked={sel.has(mp.id)} onChange={() => toggle(mp.id)} data-testid={`master-check-${mp.id}`} />
                <div className="pr-6">
                  <p className="font-mono text-[9px] tracking-widest text-white/40 uppercase">{mp.subcategory} · {mp.brand}</p>
                  <p className="font-display text-lg tracking-tight leading-tight mt-1">
                    {mp.product_name}{mp.variant && mp.variant !== mp.product_name ? ` · ${mp.variant}` : ""}
                  </p>
                  <p className="mt-1 text-white/50 text-xs">
                    {mp.net_content_ml ? (mp.net_content_ml >= 1000 ? `${mp.net_content_ml / 1000} L` : `${mp.net_content_ml} ml`) : ""}
                    {mp.package_type ? ` · ${mp.package_type}` : ""}
                    {mp.returnable ? " · retornable" : ""}
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-4 py-2 rounded-full border border-white/10 text-white/70 text-xs font-mono uppercase tracking-widest hover:text-white disabled:opacity-30">Anterior</button>
            <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-4 py-2 rounded-full border border-white/10 text-white/70 text-xs font-mono uppercase tracking-widest hover:text-white disabled:opacity-30">Siguiente</button>
          </div>
        )}
      </div>

      <Toaster theme="dark" position="top-center" />
    </main>
  );
}

function FilterChip({ label, active, onClick, tid }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${active ? "border-white bg-white text-black" : "border-white/10 text-white/60 hover:text-white hover:border-white/30"}`}
      data-testid={tid}
    >
      {label}
    </button>
  );
}
